import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Search, Eye, Download, FileText, Users, Shield } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth, formatApiErrorDetail } from '../../context/AuthContext';
import SwitchViewMenu from '../../components/SwitchViewMenu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { CSAInvites } from './CSAInvites';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CSADashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ stats: {}, submissions: [] });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/csa/dashboard`, { withCredentials: true });
        setData(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to load submissions');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.submissions;
    return data.submissions.filter((s) =>
      (s.client_name || '').toLowerCase().includes(q) ||
      (s.client_email || '').toLowerCase().includes(q) ||
      (s.advisor_name || '').toLowerCase().includes(q) ||
      (s.advisor_email || '').toLowerCase().includes(q)
    );
  }, [data.submissions, query]);

  const handleLogout = async () => {
    await logout();
    navigate('/advisor/login');
  };

  const handleDownload = async (submissionId, clientLast) => {
    setDownloadingId(submissionId);
    try {
      const res = await axios.get(`${API}/csa/submissions/${submissionId}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      const safe = (clientLast || 'client').replace(/[^a-z0-9-_]/gi, '');
      a.href = url;
      a.download = `SteelPeak-Client-Profile-${safe}-${submissionId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5b93d6]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="csa-dashboard">
      <header className="sticky top-0 z-40 bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/stpk-logo.jpg" alt="SteelPeak" className="w-8 h-8 rounded-md shadow-sm" />
            <div>
              <h1 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.03em' }}>
                <Shield className="w-4 h-4 text-[#5b93d6]" /> Client Service Associate
              </h1>
              <p className="text-xs text-white/80">Review Client Profiles and resend client invitations</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-white">
            <span className="text-sm hidden sm:inline">{user?.name || user?.email}</span>
            <SwitchViewMenu current="csa" roleLabel="CSA" />
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="csa-logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-8 space-y-6">
        <Tabs defaultValue="submissions">
          <TabsList className="mb-6" data-testid="csa-portal-tabs">
            <TabsTrigger value="submissions" data-testid="csa-submissions-tab">Submitted Profiles</TabsTrigger>
            <TabsTrigger value="invites" data-testid="csa-invites-tab">Client Invites</TabsTrigger>
          </TabsList>
          <TabsContent value="submissions" className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>Submitted Client Profiles</h2>
          <p className="text-sm text-[#475569] mt-1">Search by client name, email, or advisor. View or download each submission's PDF.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-[#475569] uppercase tracking-wider">Total Submissions</p>
                <p className="text-3xl font-bold text-[#0f172a] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="csa-stat-total">{data.stats.total_submissions ?? 0}</p>
              </div>
              <FileText className="w-6 h-6 text-[#5b93d6]" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-[#475569] uppercase tracking-wider">Advisors Represented</p>
                <p className="text-3xl font-bold text-[#0f172a] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{data.stats.total_advisors_with_submissions ?? 0}</p>
              </div>
              <Users className="w-6 h-6 text-[#5b93d6]" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#0f2444] via-[#1e3a63] to-[#26518c] text-white rounded-lg shadow-sm p-4">
            <p className="text-xs text-white/80 uppercase tracking-wider">Showing</p>
            <p className="text-3xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{filtered.length}<span className="text-lg text-white/70 font-normal"> of {data.submissions.length}</span></p>
            <p className="text-xs text-white/70 mt-1">{query ? 'matching your search' : 'unfiltered'}</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8] pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by client name, email, or advisor…"
            className="pl-9"
            data-testid="csa-search-input"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full portal-table">
              <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Advisor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Client Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#475569] uppercase">Submitted</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[#475569] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {filtered.length === 0 ? (
                  <tr><td colSpan="5" className="px-6 py-8 text-center text-[#475569]">{query ? 'No matching submissions.' : 'No submissions yet.'}</td></tr>
                ) : filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-[#f8fafc]" data-testid={`csa-submission-${s.id}`}>
                    <td className="px-6 py-3 text-sm text-[#0f172a] font-medium">{s.advisor_name}</td>
                    <td className="px-6 py-3 text-sm text-[#0f172a]">{s.client_name || '—'}</td>
                    <td className="px-6 py-3 text-sm text-[#475569]">{s.client_email}</td>
                    <td className="px-6 py-3 text-sm text-[#475569]">{new Date(s.submitted_at).toLocaleDateString()}</td>
                    <td className="px-6 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/advisor/submission/${s.id}`, { state: { from: 'csa' } })} data-testid={`csa-view-${s.id}`}>
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(s.id, s.client_name?.split(' ').pop())}
                          disabled={downloadingId === s.id}
                          data-testid={`csa-download-${s.id}`}
                        >
                          <Download className={`w-4 h-4 mr-1 ${downloadingId === s.id ? 'animate-pulse' : ''}`} /> PDF
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
          </TabsContent>
          <TabsContent value="invites"><CSAInvites /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
